import { useId, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AuthShell } from '../components/auth/AuthShell.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { PasswordInput } from '../components/ui/password-input.js';
import { BankSelect } from '../components/account/BankSelect.js';
import { useAuthStore } from '../stores/auth.js';
import { registerMutation, loginMutation, fetchMeMutation } from '../features/auth/api.js';
import { mapApiError } from '../lib/api/error.js';
import { readAffCode } from '../lib/aff-code.js';
import { DEFAULT_COUNTRY_ISO, findCountryByIso } from '../lib/country-codes.js';
import { useBrandingStore } from '../stores/branding.js';
import { useBankCurrency, useBankMasters } from '../features/finance/api.js';
import { TurnstileWidget } from '../components/captcha/TurnstileWidget.js';
import { PASSWORD_MIN_LENGTH, passwordErrorKey } from '../features/auth/password-policy.js';
import {
  BANK_ACCOUNT_NO_MAX,
  hasFieldErrors,
  localDigitsFor,
  serverFieldErrors,
  validateBankAccountNo,
  validateNamePart,
  validateUsername,
  type ServerFieldErrors,
} from '../features/auth/register-rules.js';
import { useTranslation } from 'react-i18next';

interface RegisterSearch {
  ref?: string;
}

export const Route = createFileRoute('/_auth/register')({
  validateSearch: (search: Record<string, unknown>): RegisterSearch => {
    const ref = typeof search.ref === 'string' ? search.ref : undefined;
    return ref ? { ref } : {};
  },
  component: RegisterPage,
});

export const E164_RE = /^\+[1-9]\d{7,14}$/;

// Accepts a fully-qualified number (leading '+' or '00') as-is; otherwise
// treats the input as a local number for the selected country and strips a
// single trunk '0'.
// ponytail: heuristic E.164 conversion (strip one leading trunk 0), not full
// libphonenumber per-country parsing — upgrade if multi-country edge cases surface.
export function toE164(raw: string, dial: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('+')) {
    const digits = trimmed.replace(/\D/g, '');
    return digits ? `+${digits}` : '';
  }
  const allDigits = trimmed.replace(/\D/g, '');
  if (allDigits.startsWith('00')) {
    const digits = allDigits.slice(2);
    return digits ? `+${digits}` : '';
  }
  const digits = allDigits.replace(/^0+/, '');
  return digits ? `${dial}${digits}` : '';
}

interface RegisterFormValues {
  username: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  bankCode: string;
  bankAccountNo: string;
  referralCode: string;
  agreed: boolean;
}

const LABEL = 'text-[11px] font-bold uppercase tracking-wide text-[var(--text-secondary)]';
const FIELD = 'flex flex-col gap-1';

function FieldErrors({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;
  return (
    <div className="flex flex-col gap-0.5">
      {messages.map((message) => (
        <span key={message} className="text-[11px] text-[var(--danger)]">
          {message}
        </span>
      ))}
    </div>
  );
}

function RegisterPage() {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const setSubject = useAuthStore((s) => s.setSubject);
  const { t } = useTranslation('auth');
  const captcha = useBrandingStore((s) => s.branding?.captcha);
  const captchaOn = Boolean(captcha?.enabled && captcha?.siteKey);
  const [captchaToken, setCaptchaToken] = useState<string | undefined>(undefined);
  const { ref: rawRef } = Route.useSearch();
  const referralCode = rawRef?.trim().toUpperCase() || undefined;
  // Phone country code is fixed by the site's configured country (backoffice
  // /site → branding); the member just types their local number. Falls back to
  // TH while a cached site-config snapshot predates the field.
  const countryIso = useBrandingStore((s) => s.branding?.phoneCountry ?? DEFAULT_COUNTRY_ISO);
  // A refusal that names its field is shown under that input; anything else
  // still falls back to a toast.
  const [serverErrors, setServerErrors] = useState<ServerFieldErrors>({});
  const bankAccountNoId = useId();

  const country = findCountryByIso(countryIso) ?? findCountryByIso(DEFAULT_COUNTRY_ISO)!;
  const usernameDigits = localDigitsFor(countryIso);

  const banksQuery = useBankMasters({ currency: useBankCurrency() });
  const banks = banksQuery.data?.items ?? [];

  const submitMutation = useMutation({
    mutationFn: (value: RegisterFormValues) => {
      const firstName = value.firstName.trim();
      const lastName = value.lastName.trim();
      const affCode = readAffCode();
      return registerMutation({
        username: value.username,
        password: value.password,
        // The separate phone box is gone: the username IS the number, kept in
        // its own column so the backoffice can still edit it independently.
        phone: toE164(value.username, country.dial),
        phoneCountryCode: country.dial,
        firstName,
        lastName,
        bankCode: value.bankCode,
        bankAccountNo: value.bankAccountNo,
        bankAccountName: `${firstName} ${lastName}`.trim(),
        ...(value.referralCode.trim() ? { referralCode: value.referralCode.trim().toUpperCase() } : {}),
        ...(affCode ? { affCode } : {}),
        ...(captchaToken ? { captchaToken } : {}),
      });
    },
    onSuccess: async (_data, value) => {
      // Auto-login after successful registration. Use the chosen username as the
      // identifier — phone-based login was removed per product direction.
      try {
        const loginResult = await loginMutation({ identifier: value.username, password: value.password });
        setAuth({
          accessToken: loginResult.accessToken,
          refreshToken: loginResult.refreshToken ?? null,
        });
        const user = loginResult.user;
        if (user?.id) {
          setSubject({
            id: user.id,
            kind: 'player',
            agentId: user.agentId ?? '',
            currency: 'THB',
            displayName: user.username,
          });
        } else {
          try {
            const subject = await fetchMeMutation();
            setSubject(subject);
          } catch {
            // non-fatal
          }
        }
      } catch {
        // If auto-login fails, send to login page
        void navigate({ to: '/login' });
        return;
      }
      void navigate({ to: '/' });
    },
    onError: (err) => {
      // `defaultValue` is what stops an uncatalogued code reaching a player as
      // the literal string "MEMBER-001": i18next returns the key when it cannot
      // resolve one.
      const fieldErrors = serverFieldErrors(err, (code) =>
        t(`errors:${code}`, { defaultValue: t('errors:unknown') }),
      );
      if (hasFieldErrors(fieldErrors)) {
        setServerErrors(fieldErrors);
        return;
      }
      toast.error(mapApiError(err, t));
    },
  });

  const form = useForm<RegisterFormValues>({
    defaultValues: {
      username: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      bankCode: '',
      bankAccountNo: '',
      referralCode: referralCode ?? '',
      agreed: true,
    },
    onSubmit: async ({ value }) => {
      setServerErrors({});
      await submitMutation.mutateAsync(value);
    },
  });

  const clearServerError = (field: keyof ServerFieldErrors) => {
    setServerErrors((s) => (s[field] ? { ...s, [field]: undefined } : s));
  };

  const usernameError = (value: string): string | undefined => {
    const error = validateUsername(value, countryIso, (local) =>
      E164_RE.test(toE164(local, country.dial)),
    );
    return error ? t(error.key, error.values ?? {}) : undefined;
  };
  const passwordError = (value: string): string | undefined => {
    if (!value) return t('register.errors.passwordRequired');
    const key = passwordErrorKey(value);
    return key ? t(key, { min: PASSWORD_MIN_LENGTH }) : undefined;
  };
  const bankAccountError = (value: string): string | undefined => {
    const error = validateBankAccountNo(value);
    return error ? t(error.key) : undefined;
  };
  const nameError = (value: string, requiredKey: string): string | undefined => {
    const error = validateNamePart(value, requiredKey);
    return error ? t(error.key) : undefined;
  };

  return (
    <AuthShell compact>
      <div>
        <h1 className="text-2xl md:text-3xl font-bold leading-tight">{t('register.title')}</h1>
        <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">{t('register.subtitle')}</p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-2.5"
      >
        <form.Field name="username" validators={{ onChange: ({ value }) => usernameError(value) }}>
          {(field) => {
            const messages = [
              ...(field.state.meta.errors[0] ? [field.state.meta.errors[0] as string] : []),
              ...(serverErrors.username ?? []),
            ];
            return (
              <div className={FIELD}>
                <label className={LABEL}>{t('register.username')}</label>
                <Input
                  placeholder={
                    usernameDigits
                      ? t('register.usernamePlaceholder', { digits: usernameDigits })
                      : t('register.phonePlaceholder')
                  }
                  inputMode="numeric"
                  autoComplete="username"
                  {...(usernameDigits ? { maxLength: usernameDigits } : {})}
                  value={field.state.value}
                  onChange={(e) => {
                    // Digits only, per the field rule — typed or pasted.
                    field.handleChange(e.target.value.replace(/\D/g, ''));
                    clearServerError('username');
                  }}
                  onBlur={field.handleBlur}
                  error={messages.length > 0}
                />
                <FieldErrors messages={messages} />
              </div>
            );
          }}
        </form.Field>

        <div className="grid grid-cols-2 gap-2.5">
          <form.Field name="password" validators={{ onChange: ({ value }) => passwordError(value) }}>
            {(field) => {
              const messages = [
                ...(field.state.meta.errors[0] ? [field.state.meta.errors[0] as string] : []),
                ...(serverErrors.password ?? []),
              ];
              return (
                <div className={FIELD}>
                  <label className={LABEL}>{t('register.password')}</label>
                  <PasswordInput
                    placeholder={t('register.passwordPlaceholder', { min: PASSWORD_MIN_LENGTH })}
                    autoComplete="new-password"
                    value={field.state.value}
                    onChange={(e) => {
                      field.handleChange(e.target.value);
                      clearServerError('password');
                    }}
                    onBlur={field.handleBlur}
                    error={messages.length > 0}
                  />
                  <FieldErrors messages={messages} />
                </div>
              );
            }}
          </form.Field>

          <form.Field
            name="confirmPassword"
            validators={{
              onChangeListenTo: ['password'],
              onChange: ({ value }) => {
                if (!value) return t('register.errors.confirmRequired');
                if (value !== form.getFieldValue('password')) return t('register.errors.passwordMismatch');
                return undefined;
              },
            }}
          >
            {(field) => {
              const error = field.state.meta.errors[0] as string | undefined;
              return (
                <div className={FIELD}>
                  <label className={LABEL}>{t('register.confirmPassword')}</label>
                  <PasswordInput
                    placeholder={t('register.confirmPasswordPlaceholder')}
                    autoComplete="new-password"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    error={!!error}
                  />
                  <FieldErrors messages={error ? [error] : []} />
                </div>
              );
            }}
          </form.Field>
        </div>

        <div className="flex flex-col gap-1">
          <label className={LABEL}>{t('register.accountName')}</label>
          <div className="grid grid-cols-2 gap-2.5">
            <form.Field
              name="firstName"
              validators={{
                onChange: ({ value }) => nameError(value, 'register.errors.firstNameRequired'),
              }}
            >
              {(field) => {
                const error = field.state.meta.errors[0] as string | undefined;
                return (
                  <div className={FIELD}>
                    <Input
                      placeholder={t('register.firstNamePlaceholder')}
                      autoComplete="given-name"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={!!error}
                    />
                    <FieldErrors messages={error ? [error] : []} />
                  </div>
                );
              }}
            </form.Field>

            <form.Field
              name="lastName"
              validators={{
                onChange: ({ value }) => nameError(value, 'register.errors.lastNameRequired'),
              }}
            >
              {(field) => {
                const error = field.state.meta.errors[0] as string | undefined;
                return (
                  <div className={FIELD}>
                    <Input
                      placeholder={t('register.lastNamePlaceholder')}
                      autoComplete="family-name"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      error={!!error}
                    />
                    <FieldErrors messages={error ? [error] : []} />
                  </div>
                );
              }}
            </form.Field>
          </div>
        </div>

        <form.Field
          name="bankCode"
          validators={{
            onChange: ({ value }) => (value ? undefined : t('register.errors.bankRequired')),
          }}
        >
          {(field) => {
            const error = field.state.meta.errors[0] as string | undefined;
            return (
              <div className={FIELD}>
                <label className={LABEL}>{t('register.bank')}</label>
                <BankSelect
                  banks={banks}
                  value={field.state.value}
                  onChange={(code) => field.handleChange(code.toUpperCase())}
                  onBlur={field.handleBlur}
                  loading={banksQuery.isPending}
                  placeholder={t('register.bankPlaceholder')}
                  loadingLabel={t('register.bankLoading')}
                />
                <FieldErrors messages={error ? [error] : []} />
              </div>
            );
          }}
        </form.Field>

        <div className="grid grid-cols-2 gap-2.5">
          <form.Field
            name="bankAccountNo"
            validators={{ onChange: ({ value }) => bankAccountError(value) }}
          >
            {(field) => {
              const messages = [
                ...(field.state.meta.errors[0] ? [field.state.meta.errors[0] as string] : []),
                ...(serverErrors.bankAccountNo ?? []),
              ];
              return (
                <div className={FIELD}>
                  <label htmlFor={bankAccountNoId} className={LABEL}>{t('register.bankAccountNumber')}</label>
                  <Input
                    id={bankAccountNoId}
                    placeholder={t('register.bankAccountNumberPlaceholder')}
                    inputMode="numeric"
                    maxLength={BANK_ACCOUNT_NO_MAX}
                    value={field.state.value}
                    onChange={(e) => {
                      field.handleChange(e.target.value.replace(/\D/g, ''));
                      clearServerError('bankAccountNo');
                    }}
                    onBlur={field.handleBlur}
                    error={messages.length > 0}
                  />
                  <FieldErrors messages={messages} />
                </div>
              );
            }}
          </form.Field>

          {/* ponytail: no code validation — the backend deliberately ignores
              unknown codes so registration never fails on a typo. */}
          <form.Field name="referralCode">
            {(field) => (
              <div className={FIELD}>
                <label className={LABEL}>{t('register.referral')}</label>
                <Input
                  autoComplete="off"
                  className="font-mono uppercase"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value.toUpperCase())}
                  onBlur={field.handleBlur}
                />
              </div>
            )}
          </form.Field>
        </div>

        <form.Field
          name="agreed"
          validators={{ onChange: ({ value }) => (value ? undefined : t('register.errors.termsNotAgreed')) }}
        >
          {(field) => {
            const error = field.state.meta.errors[0] as string | undefined;
            return (
              <div className="flex flex-col gap-1">
                <label className="flex items-start gap-2.5 text-[12px] text-[var(--text-secondary)] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={field.state.value}
                    onChange={(e) => field.handleChange(e.target.checked)}
                    className="mt-0.5 accent-[var(--primary)]"
                  />
                  <span>{t('register.terms')}</span>
                </label>
                <FieldErrors messages={error ? [error] : []} />
              </div>
            );
          }}
        </form.Field>

        {captchaOn && captcha?.siteKey && (
          <TurnstileWidget siteKey={captcha.siteKey} onSolve={setCaptchaToken} />
        )}

        <Button
          type="submit"
          size="lg"
          glow
          fullWidth
          disabled={submitMutation.isPending || (captchaOn && !captchaToken)}
        >
          {submitMutation.isPending ? t('register.submitting') : t('register.submit')}
        </Button>
      </form>

      <div className="flex flex-col items-center gap-1 text-[13px] text-[var(--text-secondary)]">
        <span>
          {t('register.haveAccount')}{' '}
          <Link to="/login" className="text-[var(--primary)] font-semibold">
            {t('register.signIn')}
          </Link>
        </span>
        <Link to="/" className="text-[var(--text-muted)] hover:text-[var(--text-primary)]">
          {t('backToLobby')}
        </Link>
      </div>
    </AuthShell>
  );
}
